// src/pages/ListingDetail.js
// Page détail d'une annonce avec partage social et Open Graph complet - NE PAS MODIFIER

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PriceBadge from '../components/listing/PriceBadge';
import ContactSheet from '../components/reservation/ContactSheet';
import ShareSheet from '../components/listing/ShareSheet';
import { useListings } from '../hooks/useListings';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import { formatPrice } from '../utils/formatPrice';

const ListingDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isFavorite, addToFavorites, removeFromFavorites } = useListings();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    const loadListing = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const result = await apiClient.get(`/endpoints/listings.php?action=detail&slug=${encodeURIComponent(slug)}`);
        setListing(result);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadListing();
    setCurrentImageIndex(0);
  }, [slug]);

  const images = listing?.images && listing.images.length > 0 ? listing.images : ['/images/placeholder.png'];

  const goToImage = (index) => {
    if (index < 0) index = images.length - 1;
    if (index >= images.length) index = 0;
    setCurrentImageIndex(index);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      goToImage(diff > 0 ? currentImageIndex + 1 : currentImageIndex - 1);
    }
    touchStartX.current = null;
  };

  const styles = {
    stateContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '48px', textAlign: 'center', color: theme.textSecondary, backgroundColor: theme.bg },
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', paddingBottom: '80px', backgroundColor: theme.bg },
    topBar: { position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', zIndex: 20 },
    topBarRight: { display: 'flex', gap: '8px' },
    iconButton: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)', border: 'none', color: '#FFFFFF', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    imageContainer: { position: 'relative', width: '100%', height: '300px', backgroundColor: theme.bgInput, overflow: 'hidden' },
    image: { width: '100%', height: '100%', objectFit: 'cover', userSelect: 'none' },
    navArrow: {
      position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '36px', height: '36px',
      borderRadius: '50%', backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)', border: 'none',
      color: '#FFFFFF', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 15,
    },
    navArrowLeft: { left: '12px' },
    navArrowRight: { right: '12px' },
    imageCounter: {
      position: 'absolute', top: '16px', right: '16px', padding: '4px 10px',
      backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)', borderRadius: '12px',
      color: '#FFFFFF', fontSize: '12px', fontWeight: '600',
    },
    imageDots: { position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px' },
    dot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.5)', cursor: 'pointer' },
    dotActive: { backgroundColor: theme.gold, width: '20px', borderRadius: '4px' },
    content: { padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '16px' },
    title: { fontSize: '22px', fontWeight: '700', color: theme.textPrimary, lineHeight: '1.3' },
    location: { fontSize: '14px', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' },
    priceRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    details: { display: 'flex', gap: '16px', padding: '16px', backgroundColor: theme.bgCard, borderRadius: '12px', border: `1px solid ${theme.border}` },
    detailItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: 1 },
    detailValue: { fontSize: '18px', fontWeight: '600', color: theme.gold },
    detailLabel: { fontSize: '12px', color: theme.textSecondary },
    section: { display: 'flex', flexDirection: 'column', gap: '8px' },
    sectionTitle: { fontSize: '16px', fontWeight: '600', color: theme.textPrimary },
    description: { fontSize: '14px', color: theme.textSecondary, lineHeight: '1.6' },
    amenities: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    amenity: { padding: '6px 12px', backgroundColor: theme.bgCard, borderRadius: '8px', fontSize: '13px', color: theme.textPrimary, border: `1px solid ${theme.border}` },
    bottomBar: { position: 'fixed', bottom: '64px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '480px', display: 'flex', gap: '10px', padding: '12px 16px', backgroundColor: theme.bg, borderTop: `1px solid ${theme.border}`, zIndex: 90 },
    contactButton: { flex: 1, padding: '14px', backgroundColor: '#25D366', border: 'none', borderRadius: '10px', color: '#FFFFFF', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  };

  if (loading) {
    return <div style={styles.stateContainer}>{t('common.loading')}</div>;
  }

  if (notFound || !listing) {
    return (
      <div style={styles.stateContainer}>
        <div>
          {t('errors.notFound')}
          <br />
          <button style={{ marginTop: '16px', padding: '10px 20px', backgroundColor: theme.gold, border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: '700', cursor: 'pointer' }} onClick={() => navigate('/')}>
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const favorite = isFavorite(listing.id);
  const priceType = listing.price_type || listing.priceType;
  const pageUrl = `https://fynho.com/listing/${listing.slug}`;
  const priceLabel = formatPrice(listing.price);
  const ogDescription = listing.description
    ? listing.description.substring(0, 160)
    : `${listing.title} - ${listing.quartier}, ${listing.city} - ${priceLabel} FCFA. Découvrez cette annonce sur Fynho.`;

  const handleFavoriteClick = () => {
    if (favorite) removeFromFavorites(listing.id);
    else addToFavorites(listing.id);
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>{listing.title} - {listing.quartier} | Fynho</title>
        <meta name="description" content={ogDescription} />

        {/* Open Graph (WhatsApp, Facebook, iMessage) */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${listing.title} - ${listing.quartier}, ${listing.city}`} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={images[0]} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Fynho" />
        <meta property="product:price:amount" content={listing.price} />
        <meta property="product:price:currency" content="XOF" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${listing.title} - ${listing.quartier}, ${listing.city}`} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={images[0]} />

        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <div style={styles.topBar}>
        <button style={styles.iconButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.topBarRight}>
          <button style={styles.iconButton} onClick={() => setShowShareSheet(true)}>📤</button>
          <button style={styles.iconButton} onClick={handleFavoriteClick}>{favorite ? '❤️' : '🤍'}</button>
        </div>
      </div>

      <div
        style={styles.imageContainer}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img src={images[currentImageIndex]} alt={listing.title} style={styles.image} draggable={false} />

        {images.length > 1 && (
          <>
            <button style={{ ...styles.navArrow, ...styles.navArrowLeft }} onClick={() => goToImage(currentImageIndex - 1)}>‹</button>
            <button style={{ ...styles.navArrow, ...styles.navArrowRight }} onClick={() => goToImage(currentImageIndex + 1)}>›</button>
            <div style={styles.imageCounter}>{currentImageIndex + 1} / {images.length}</div>
            <div style={styles.imageDots}>
              {images.map((_, index) => (
                <div
                  key={index}
                  style={{ ...styles.dot, ...(index === currentImageIndex ? styles.dotActive : {}) }}
                  onClick={() => goToImage(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div style={styles.content}>
        <div style={styles.title}>{listing.title}</div>
        <div style={styles.location}>📍 {listing.quartier}, {listing.city}</div>
        <div style={styles.priceRow}>
          <PriceBadge price={listing.price} priceType={priceType} size="large" />
        </div>

        {(listing.bedrooms || listing.bathrooms || listing.area) && (
          <div style={styles.details}>
            {listing.bedrooms && (
              <div style={styles.detailItem}>
                <span style={styles.detailValue}>{listing.bedrooms}</span>
                <span style={styles.detailLabel}>🛏️ {t('listing.bedrooms')}</span>
              </div>
            )}
            {listing.bathrooms && (
              <div style={styles.detailItem}>
                <span style={styles.detailValue}>{listing.bathrooms}</span>
                <span style={styles.detailLabel}>🚿 {t('listing.bathrooms')}</span>
              </div>
            )}
            {listing.area && (
              <div style={styles.detailItem}>
                <span style={styles.detailValue}>{listing.area}</span>
                <span style={styles.detailLabel}>📐 m²</span>
              </div>
            )}
          </div>
        )}

        {listing.description && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{t('listing.description')}</div>
            <div style={styles.description}>{listing.description}</div>
          </div>
        )}

        {listing.amenities && listing.amenities.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{t('listing.amenities')}</div>
            <div style={styles.amenities}>
              {listing.amenities.map((amenity, index) => (
                <div key={index} style={styles.amenity}>✓ {amenity}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={styles.bottomBar}>
        <button style={styles.contactButton} onClick={() => setShowContactSheet(true)}>
          💬 {t('listing.contact')}
        </button>
      </div>

      <ContactSheet
        open={showContactSheet}
        onClose={() => setShowContactSheet(false)}
        contactPhone={listing.contact_phone}
        listingTitle={listing.title}
        listingSlug={listing.slug}
      />

      <ShareSheet
        open={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        title={`${listing.title} - ${listing.quartier}, ${listing.city}`}
        url={pageUrl}
        imageUrl={images[0]}
      />
    </div>
  );
};

export default ListingDetail;